/**
 * Server refusals worth explaining. The no-escalation guard (admin/guards.py) answers 403
 * "you cannot <verb> more than you hold yourself: a, b (project …) and N more"; the UI
 * shows the verb and the missing permissions as a list instead of one long sentence.
 */
import { isApiError } from '@/lib/api/client';

export interface Escalation {
  /** "grant", "change a role", "edit someone who holds" … as the server phrased it. */
  what: string;
  missing: string[];
  /** "and 7 more" beyond the listed ones. */
  more: number;
  /** The server's refusal without the list ("you cannot grant more than you hold yourself"). */
  sentence: string;
}

const ESCALATION = /you cannot (.+?) more than you hold yourself: (.+)$/i;

export function parseEscalation(message: string): Escalation | null {
  const m = ESCALATION.exec(message.trim());
  if (!m) return null;
  let rest = m[2] ?? '';
  let more = 0;
  const tail = / and (\d+) more$/.exec(rest);
  if (tail) {
    more = Number(tail[1]);
    rest = rest.slice(0, tail.index);
  }
  // Items are separated by ", " — but never inside a parenthesis ("key (project a, b)").
  const missing: string[] = [];
  let depth = 0;
  let cur = '';
  for (let i = 0; i < rest.length; i++) {
    const ch = rest[i];
    if (ch === '(') depth++;
    if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ',' && depth === 0 && rest[i + 1] === ' ') {
      if (cur.trim()) missing.push(cur.trim());
      cur = '';
      i++;
      continue;
    }
    cur += ch;
  }
  if (cur.trim()) missing.push(cur.trim());
  const what = m[1] ?? 'grant';
  return { what, missing, more, sentence: `you cannot ${what} more than you hold yourself` };
}

export function escalationOf(error: unknown): Escalation | null {
  return isApiError(error, 403) ? parseEscalation(error.message) : null;
}
